import React, { useEffect, useState, useCallback, useRef } from 'react';
import './DisplayBoard.scss';
import { getTasksAssignedByUser, getTasksAssignedToUser } from '../../services/api/TaskApi';
import { getDisplayBoardData } from '../../services/api/DisplayBoardApi';
import { getHierarchy } from '../../services/api/HierarchyApi';
import { getNoteByUser } from '../../services/api/NoteApi';
import CalendarWidget from '../../components/CalendarWidget/CalendarWidget';
import TaskStatsWidget from '../../components/TaskStatsWidget/TaskStatsWidget';
import UpcomingListWidget from '../../components/UpcomingListWidget/UpcomingListWidget';
import ChartsWidget from '../../components/ChartsWidget/ChartsWidget';
import { exportDashboardToPDF } from '../../components/exportDashboardToPDF';
import ExportModal from '../../components/ExportModal/ExportModal';
import { useLoading } from "../../context/LoadingContext";
import API from '../../services/api/axios.js';
import { useAuth } from '../../context/AuthContext.js';
import SubscriptionExpiredModal from '../../components/SubscriptionExpiredModal/SubscriptionExpiredModal.js';

export const DisplayBoard = () => {
  const [dashboardData, setDashboardData] = useState({});
  const [assignedByData, setAssignedByData] = useState(null);
  const [assignedToData, setAssignedToData] = useState(null);
  const [notesData, setNotesData] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSubscriptionExpiredModal, setShowSubscriptionExpiredModal] = useState(false);
  
  // New state for member selection
  const [hierarchyUsers, setHierarchyUsers] = useState([]);
  const [selectedMemberId, setSelectedMemberId] = useState('');
  const [showMemberDropdown, setShowMemberDropdown] = useState(false);
  
  const { showLoading, hideLoading } = useLoading();
  const { company, role, logout } = useAuth();

  // Refs to prevent multiple loads
  const isLoadingRef = useRef(false);
  const hasFetchedHierarchy = useRef(false);
  const hasCheckedSubscription = useRef(false);

  // Check user role from localStorage
  const checkUserRole = () => {
    const role = localStorage.getItem('role');
    return role === 'admin' || role === 'moderator';
  };

  // Function to check if subscription is expired
  const isSubscriptionExpired = useCallback(() => {
    if (!company?.subscription?.currentPeriodEnd) return false;

    const endDate = new Date(company.subscription.currentPeriodEnd);
    const currentDate = new Date();
    
    return currentDate > endDate;
  }, [company?.subscription]);

  // Fetch hierarchy data
  const fetchHierarchy = useCallback(async () => {
    try {
      const response = await getHierarchy();
      console.log('Hierarchy Response:', response);
      if (response && response.data && response.data.users) {
        // Get current user's email from localStorage
        let currentUserEmail = null;
        try {
          const userString = localStorage.getItem('user');
          console.log('User string from localStorage:', userString);
          console.log('Type of userString:', typeof userString);
          
          if (userString) {
            // Handle case where the string might be double-encoded
            let parsedUser;
            try {
              parsedUser = JSON.parse(userString);
              console.log('First parse result:', parsedUser);
              console.log('Type after first parse:', typeof parsedUser);
              
              // If it's still a string, parse again
              if (typeof parsedUser === 'string') {
                parsedUser = JSON.parse(parsedUser);
                console.log('Second parse result:', parsedUser);
              }
            } catch (e) {
              console.error('JSON parse error:', e);
              parsedUser = null;
            }
            
            if (parsedUser && typeof parsedUser === 'object') {
              console.log('Final parsed user object:', parsedUser);
              console.log('User object keys:', Object.keys(parsedUser));
              currentUserEmail = parsedUser.email;
              console.log('Extracted current user email:', currentUserEmail);
            }
          }
        } catch (error) {
          console.error('Error parsing user from localStorage:', error);
        }
        
        // Log all user emails for debugging
        console.log('All user emails in hierarchy:', response.data.users.map(u => ({ name: u.name, email: u.email, id: u._id })));
        
        // Filter out the current user from the hierarchy only if we have a valid email
        let filteredUsers = response.data.users;
        if (currentUserEmail) {
          filteredUsers = response.data.users.filter(user => {
            const isCurrentUser = user.email === currentUserEmail;
            console.log(`User: ${user.name} (${user.email}) - Is current user: ${isCurrentUser}`);
            return !isCurrentUser;
          });
          console.log('Successfully filtered current user');
        } else {
          console.warn('No current user email found, showing all users');
        }
        
        setHierarchyUsers(filteredUsers);
        console.log('Filtered Hierarchy Users (excluding current user):', filteredUsers);
        console.log('Total users before filtering:', response.data.users.length);
        console.log('Total users after filtering:', filteredUsers.length);
        
        // Update showMemberDropdown based on filtered users and user role
        setShowMemberDropdown(checkUserRole() && filteredUsers.length > 0);
      }
    } catch (error) {
      console.error('Error fetching hierarchy:', error);
      // If there's an error fetching hierarchy, hide the dropdown
      setShowMemberDropdown(false);
      setHierarchyUsers([]);
    }
  }, []);

  // Fetch dashboard data for specific user
  const fetchDashboardDataByUser = async (userId) => {
    if (isLoadingRef.current) return; // Prevent concurrent calls
    
    try {
      isLoadingRef.current = true;
      showLoading('Loading member data...');
      
      console.log('Fetching data for user ID:', userId);
      
      // Use the API endpoint you mentioned that works
      const dashboardResponse = await API.get(`case-dashboard/by-user/${userId}`);
      console.log('Dashboard response for user:', dashboardResponse.data);
      
      // Make user-specific API calls for tasks and notes
      const [assignedBy, assignedTo, notes] = await Promise.all([
        API.get(`tasks/assigned-by/${userId}`).catch(() => ({ data: { tasks: [], totalTasks: 0 } })),
        API.get(`tasks/assigned-to/${userId}`).catch(() => ({ data: { tasks: [], totalTasks: 0 } })),
        API.get(`notes/by-user/${userId}`).catch(() => ({ data: { notes: [] } }))
      ]);

      console.log('AssignedBy response for user:', assignedBy.data);
      console.log('AssignedTo response for user:', assignedTo.data);
      console.log('Notes response for user:', notes.data);

      // Compare old vs new data
      console.log('OLD Dashboard Data:', dashboardData);
      console.log('NEW Dashboard Data:', dashboardResponse.data);

      // Set the user-specific dashboard data
      setDashboardData(dashboardResponse.data);
      setAssignedByData(assignedBy.data);
      setAssignedToData(assignedTo.data);
      setNotesData(notes.data.notes || notes.data);
      
      hideLoading();
    } catch (error) {
      console.error('Error fetching dashboard data by user:', error);
      hideLoading();
      
      // Fallback to default data if API fails
      console.log('Falling back to default data due to error');
      fetchDefaultData();
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Fetch default dashboard data
  const fetchDefaultData = useCallback(async () => {
    if (isLoadingRef.current) return; // Prevent concurrent calls
    
    try {
      isLoadingRef.current = true;
      showLoading('Loading ...');
      
      const [dashboard, assignedBy, assignedTo, notes] = await Promise.all([
        getDisplayBoardData(),
        getTasksAssignedByUser(),
        getTasksAssignedToUser(),
        getNoteByUser()
      ]);

      setDashboardData(dashboard.data);
      setAssignedByData(assignedBy.data);
      setAssignedToData(assignedTo.data);
      setNotesData(notes.data.notes);
      
      hideLoading();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      hideLoading();
    } finally {
      isLoadingRef.current = false;
    }
  }, []); 

  // Combined initialization effect - runs only once on mount
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      console.log('DisplayBoard initializing...');
      
      // Fetch hierarchy if needed
      if (checkUserRole() && !hasFetchedHierarchy.current) {
        hasFetchedHierarchy.current = true;
        await fetchHierarchy();
      }
      
      // Fetch default data
      if (mounted && !isLoadingRef.current) {
        await fetchDefaultData();
      }
    };

    initialize();

    return () => {
      mounted = false;
      console.log('DisplayBoard cleanup');
    };
  }, []); // Empty dependency array - run only once on mount

  // Separate subscription check - runs only once
  useEffect(() => {
    if (hasCheckedSubscription.current) return;
    
    console.log('Checking subscription status...');
    
    // Check for subscription expiry from localStorage flag (set during fresh login)
    const showModalFlag = localStorage.getItem('showSubscriptionExpiredModal');
    if (showModalFlag === 'true') {
      console.log('Found subscription modal flag from login');
      hasCheckedSubscription.current = true;
      setShowSubscriptionExpiredModal(true);
      // Remove the flag after showing the modal
      localStorage.removeItem('showSubscriptionExpiredModal');
      return; // Exit early to avoid double check
    }

    // Also check current subscription status for already logged-in users
    // This handles the case when user refreshes or comes back with valid tokens
    if (company?.subscription?.currentPeriodEnd && !hasCheckedSubscription.current) {
      const expired = isSubscriptionExpired();
      if (expired && role !== 'customer') {
        console.log('Subscription expired for already logged-in user, showing modal');
        hasCheckedSubscription.current = true;
        setShowSubscriptionExpiredModal(true);
      }
    }
  }, [company?.subscription?.currentPeriodEnd, role, isSubscriptionExpired]);

  // Handle member selection change
  const handleMemberChange = (e) => {
    const selectedId = e.target.value;
    console.log('Selected member ID:', selectedId);
    setSelectedMemberId(selectedId);
    
    if (selectedId) {
      console.log('Fetching data for selected member');
      fetchDashboardDataByUser(selectedId);
    } else {
      console.log('Resetting to default data');
      // Reset to default data
      fetchDefaultData();
    }
  };

  const handleExportClick = () => {
    setShowExportModal(true);
  };

  // Updated to handle orientation parameter
  const handleExport = (dataToExport, orientation) => {
    exportDashboardToPDF(dataToExport, orientation);
  };

  return (
    <div className="display-board">
      {console.log(dashboardData, "dashboardData")}
      <div className="dashboard-header">
        <div className='main-dashboard-header'>
          <h1>Dashboard Overview</h1>
          <p>Your comprehensive view of tasks, deadlines, and progress</p>
          
          {/* Member Selection Dropdown */}
          {showMemberDropdown && (
            <div className="member-selection">
              <label htmlFor="member-select">Select Member:</label>
              <select 
                id="member-select"
                value={selectedMemberId}
                onChange={handleMemberChange}
                className="member-dropdown"
              >
                <option value="">-- View your own data --</option>
                {hierarchyUsers.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name} ({user.role}) {!user.isActive ? '(Inactive)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className='display-board-expbtn'>
          <button className="export-btn" onClick={handleExportClick}>
            Export to PDF
          </button>
        </div>
      </div>

      <div className="widgets-grid">
        <div className="widget calendar-widget">
          <div className="widget-header">
            <h3>Calendar</h3>
            <span className="widget-subtitle">Schedule & Events</span>
          </div>
          <CalendarWidget data={dashboardData} />
        </div>

        <div className="widget stats-widget">
          <div className="widget-header">
            <h3>Task Statistics</h3>
            <span className="widget-subtitle">Performance Overview</span>
          </div>
          <TaskStatsWidget assignedBy={assignedByData} assignedTo={assignedToData} />
        </div>

        <div className="widget upcoming-widget">
          <div className="widget-header">
            <h3>Upcoming Items</h3>
            <span className="widget-subtitle">Next Deadlines</span>
          </div>
          <UpcomingListWidget data={dashboardData} selectedUserId={selectedMemberId} />
        </div>
      </div>

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        dashboardData={dashboardData}
      />

      {/* Subscription Expired Modal */}
      <SubscriptionExpiredModal
        isOpen={showSubscriptionExpiredModal}
        companyId={company?._id}
        onUpgrade={() => {
          logout();
        }}
      />
    </div>
  );
};